/**
 * Practice Profile vocabulary shared between the API and the frontends.
 *
 * Only what BOTH sides must agree on lives here. The activation policy that
 * reads the threshold is the API's alone (`domain/practice-profile/activation.ts`);
 * the number itself is not, because the shelf that renders a thin practice has
 * to draw the same line the gate drew, and two definitions of "thin" means a
 * profile the medical team was warned about rendering with no warning at all.
 */

/**
 * Under this many matched publications, a practice is THIN: the gate says so
 * when the profile is created, and the shelf says so out loud to the reader.
 *
 * Story 10 — being told honestly that there is little to show makes a near-empty
 * rail read as a state of the world rather than a broken product. With
 * acquisition out of scope for this lot, some rails ARE known to be thin.
 */
export const PRACTICE_PROFILE_THIN_BELOW = 10;

/*
 * There is no bound on how many practices a reader may HOLD, and the query that
 * serves one practice would not want one.
 *
 * What is bounded is the AGGREGATE. "All practices" reads several Booleans for
 * one screen, so its cost grows with the catalogue where every other query here
 * is flat, and `ALL_PRACTICES_MAX_ARMS` below is where that stops.
 *
 * ── Why the first aggregate was deleted ──────────────────────────────────────
 * It `OR`ed the held Booleans into ONE predicate. Measured on the specialty
 * carrying 30 profiles, identical result set throughout:
 *
 *   1 practice -> 91 ms . 3 -> 69 ms . 5 -> 180 ms . 10 -> 7 775 ms . 30 -> 19 748 ms
 *
 * The request cap is 20 s, so a reader with ten practices had a feed nobody waits
 * for and a reader with more had a timeout. It also read only the first five of a
 * longer list, which is what made the chip untrue: it said "everything I
 * practise" while answering for five.
 *
 * ⚠️ That curve is HISTORY and no longer a mechanism. It was the SQL compiler
 * emitting one clause per leaf, which is fixed (`docs/adr/0003`, "one clause per
 * substrate"), so a wide disjunction no longer collapses the plan. What kills the
 * `OR` shape now is the RANKING alone — see below.
 *
 * ── What came back, and why it is a different shape ──────────────────────────
 * `allPracticesPublications` UNIONs the matched SETS instead of ORing the
 * Booleans. Each arm keeps its own `WHERE`, so the planner gets N independent
 * scans plus one dedupe. `EXPLAIN` on one arm gives the SAME plan as the shipped
 * single-practice query.
 *
 * The reason it stays that shape is the ORDER, not the cost. `or` compiles to
 * `GREATEST` and an `evidence` score is computed against ONE Boolean's leaves, so
 * it is not comparable across arms: a global sort over an OR-ed union hands the
 * widest Boolean the whole first page. The union is what makes the ordering
 * honest, which is why the aggregate does not score at all.
 *
 * Measured on the veterinary catalogue — the text-heavy one, previously the worst
 * — local (231k works), first six profiles by uid, page 1 of 20:
 *
 *   arms   feed cold   feed warm   rail cold
 *   1      52 ms       53 ms       92 ms
 *   3      133 ms      133 ms      64 ms
 *   6      344 ms      311 ms      65 ms
 *
 * The same six served ONE AT A TIME sum to 321 ms, so the union costs what its
 * arms cost and nothing extra — additive, where the `OR` was 85× its own
 * single-practice baseline by ten practices.
 *
 * Additive was true from the start; FAST only since the compiler stopped emitting
 * one clause per leaf. This same catalogue cost 11 s at six arms because
 * `avian-and-reptile-medicine` cost 6.6 s alone; it costs 68 ms. The cap below
 * therefore bounds the arm count as a PRODUCT decision, no longer as a latency
 * ceiling.
 */

/**
 * How many practices "All practices" reads.
 *
 * Six, because a specialist practises a handful of things: the reader may SAVE as
 * many as they like (`me.ts` saves the answer whole, and the per-practice feed is
 * flat in the count), and the aggregate reads the first six of their own order.
 *
 * ⚠️ The cap is SILENT — no wire field, no line on screen. That is a product
 * decision and it is worth naming, because it is the same truncation that made
 * the first aggregate lie: the line says "All practices" and answers for six.
 *
 * So it is recorded where the TEAM can see it instead. Both routes log `held`
 * next to `served`, and the feed's `practice_rail_all_practices` event carries
 * `capped` and `unsupported` besides. A reader routinely losing practices to the
 * cap is then a number rather than a silence, which is what makes raising it a
 * decision someone can take on evidence.
 */
export const ALL_PRACTICES_MAX_ARMS = 6;

/**
 * WHO decided a practitioner's practices. `agent` is the inference reading the
 * registry and the professional web together; `none` is the floor — it looked
 * and recognised nothing, which is a real answer and not a failure.
 *
 * Declared HERE rather than beside the service that produces it, because THREE
 * readers must agree on it and one of them is the webapp: the pre-selection
 * route serves it on the wire, `practice_inferences.source` stores it under a
 * database CHECK, and `invitableProfileUids` below refuses anything else. Typed
 * as `string` the comparison would still compile after a rename, and the
 * function would silently start returning `[]` — a generic email and an
 * unseeded picker for everybody, with nothing failing anywhere.
 */
export const PRACTICE_INFERENCE_SOURCES = ["agent", "none"] as const;
export type PracticeInferenceSource =
  (typeof PRACTICE_INFERENCE_SOURCES)[number];

/**
 * Narrow a stored `practice_inferences.source` to the union.
 *
 * Prisma types the column `string` because it is TEXT — the vocabulary is
 * enforced by a CHECK the client cannot see. So a value outside the union means
 * the constraint is missing from THIS database, which is the exact failure the
 * double declaration (migration + `service/postgres/schema.sql`) exists to make
 * impossible. A loud throw is right: reading it as `none` would degrade the
 * whole campaign to generic emails and look like a modest abstention rate.
 */
export function practiceInferenceSource(raw: string): PracticeInferenceSource {
  const known = PRACTICE_INFERENCE_SOURCES.find((value) => value === raw);
  if (known === undefined)
    throw new Error(
      `Invariant: practice_inferences.source is "${raw}", which the CHECK constraint should have refused`,
    );
  return known;
}

/**
 * WHICH writer stored a `practice_inferences` row: the inference campaign, or
 * the signup pre-selection of a signed-in practitioner. Stored under a CHECK,
 * and served by `GET /webapp/private/practice-profiles/me` so the picker can
 * report where its pre-selection came from.
 */
export const PRACTICE_INFERENCE_ORIGINS = ["campaign", "signup"] as const;
export type PracticeInferenceOrigin =
  (typeof PRACTICE_INFERENCE_ORIGINS)[number];

/** Narrow a stored `practice_inferences.origin`. Same reasoning as
 * `practiceInferenceSource`: an unknown value means the CHECK is missing. */
export function practiceInferenceOrigin(raw: string): PracticeInferenceOrigin {
  const known = PRACTICE_INFERENCE_ORIGINS.find((value) => value === raw);
  if (known === undefined)
    throw new Error(
      `Invariant: practice_inferences.origin is "${raw}", which the CHECK constraint should have refused`,
    );
  return known;
}

/** The `origin` and `campaign_id` pair each writer stores, as the
 * `practice_inferences_origin_campaign` CHECK allows it. */
export type PracticeInferenceProvenance =
  | { readonly origin: "campaign"; readonly campaignId: string }
  | { readonly origin: "signup"; readonly campaignId: null };

/**
 * The practices a campaign invite may lead on — the ONE decision behind both
 * halves of the invitation.
 *
 * ## The invariant this exists to hold
 *
 * The email and the screen must never disagree. A reader who receives the
 * "practices" copy — which names their fields and says they are pre-ticked —
 * has to land on a picker with those fields ticked. A reader who receives the
 * "generic" copy must land on the unticked catalogue. Anything else is the
 * product contradicting itself in the space of one click.
 *
 * The two decisions are taken in different apps, at different moments, from
 * different shapes:
 *
 *  - `service/workflow/actions/fetch-practice-invite.ts` chooses the email copy
 *    from the ACTIVE profiles of the reader's catalogue Specialty;
 *  - `components/practice/PracticeProfilesEditor.tsx` chooses whether to seed
 *    the picker from what `GET /webapp/public/practice-profiles/offered` served.
 *
 * Those two sets are the same query — `status: active` + `specialtyIds has
 * <catalogue>` — and `practiceProfileOffer` maps rather than filters, so
 * `/offered` returns its query result whole. Calling one function from both
 * sides is what keeps that a fact instead of a coincidence: a filter added to
 * either side now has to be added here, where the other side reads it too.
 *
 * ## What it deliberately does NOT promise
 *
 * Agreement at ONE INSTANT. The email is composed hours before the click, so a
 * profile RETIRED in between will still produce a practices email and an
 * unseeded screen. No shared code can prevent that — the world changed between
 * the two reads — and the screen is right: it shows the catalogue as it is now.
 * The uid filtering here is what makes the failure degrade to "unticked"
 * rather than to a picker pre-ticking rows it cannot display.
 *
 * A reader who RE-DECLARES their Specialty is no longer in that category: both
 * sides now refuse an inference filed under a catalogue the reader has left,
 * which is a check about WHICH catalogue rather than which uids, so it lives in
 * the two callers rather than here.
 */
export function invitableProfileUids(args: {
  /** Only the agent's reading may seed anything; `none` is the floor and
   * pre-ticks nothing. A literal union, so a rename breaks the callers rather
   * than quietly emptying the result. */
  readonly source: PracticeInferenceSource;
  /** `practice_inferences.profile_uids`, as stored. */
  readonly inferredProfileUids: readonly string[];
  /**
   * `practice_inferences.main_profile_uid` — the one the MODEL ranked, or null.
   *
   * Required here, and ordered FIRST, because the cap below is what makes it
   * load-bearing: a reader with more than `ALL_PRACTICES_MAX_ARMS` inferred
   * practices whose main sits late in the stored list would otherwise have it
   * sliced away before any caller could promote it — and the email would then
   * lead on the Specialty, which is precisely the failure `mainProfileUid`
   * exists to prevent. Ordering before the cut is the only place that holds.
   */
  readonly mainProfileUid: string | null;
  /** The uids the reader's catalogue currently OFFERS — the API's active
   * profiles for the catalogue Specialty, or the `/offered` docs the webapp
   * holds. Same set, read from two places. */
  readonly offeredUids: ReadonlySet<string>;
}): readonly string[] {
  if (args.source !== "agent") return [];
  // Deduped: a stored row repeating a uid is a malformed write, and it would
  // otherwise spend one of the capped slots twice.
  const survivors = [...new Set(args.inferredProfileUids)].filter((uid) =>
    args.offeredUids.has(uid),
  );
  const main = args.mainProfileUid;
  // A stable partition, not a comparator. Two reasons, and the second one was
  // learned the hard way: a comparator reading a `const` declared below it
  // lands in the temporal dead zone, because `sort` calls it synchronously —
  // and `Array.prototype.sort` skips the comparator entirely at length <= 1, so
  // a one-item fixture hides the crash.
  //
  // The order this preserves for the rest is the order of `inferredProfileUids`
  // — which is the MODEL's answer, not the catalogue's: `readInferredProfiles`
  // builds the stored array by walking the model's output. The invite email
  // lists its blocks in this order, so the rest follows the model's ranking.
  const ordered =
    main === null
      ? survivors
      : [
          ...survivors.filter((uid) => uid === main),
          ...survivors.filter((uid) => uid !== main),
        ];
  return ordered.slice(0, ALL_PRACTICES_MAX_ARMS);
}
